import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import ParamsSortBy from "components/Shared/ParamsSortByTimestamp";

import { useLazyGetShipmentActionListForUserQuery } from "store/services/shipmentActionApi";
import { useAppMeta, useAuth, useTablePagination } from "hooks";

const SHIPMENT_ACTION_SORT_OPTIONS = [
    { label: "Created At (Desc)", sortBy: "createdAt", direction: -1 },
    { label: "Created At (Asc)", sortBy: "createdAt", direction: 1 },
];

const NotificationActionListScreen = () => {
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const { isVendor } = useAuth();

    const [searchParams] = useSearchParams();

    const keyword = searchParams.get("search") || "";
    const sortedBy = searchParams.get("sortBy") || "createdAt";
    const sortDirection = searchParams.get("sortDirection") || -1;

    const breadcrumbs = [{ title: "Shipment Actions" }];

    const columns = [
        {
            name: "Action ID",
            cell: (row) => (
                <HighlightedText text={row?.sourceRef?.actionId ?? "N/A"} highlight={keyword} />
            ),
        },
        {
            name: "Action Type",
            cell: (row) => (
                <HighlightedText text={row?.sourceRef?.actionType ?? "N/A"} highlight={keyword} />
            ),
        },
        {
            name: "Shipment",
            cell: (row) => row?.contextData?.shipmentId ?? "N/A",
        },
        {
            name: "Status",
            cell: (row) => row?.contextData?.actionStatus ?? "N/A",
        },
        {
            name: "Responsible",
            cell: (row) => row?.sourceRef?.responsible?.name ?? "N/A",
        },
        {
            name: "Created Date",
            cell: (row) => <AppTableDateTime date={row?.createdAt} dateOnly />,
        },
        {
            name: "Actions",
            maxWidth: "min-content",
            cell: (row) => {
                const basePath = isVendor ? "/app/vendor/shipment" : "/app/crm/shipment";
                const detailLink = `${basePath}/${row?.sourceRef?.shipment?._id}/detail?tab=action&actionId=${row?.sourceRef?._id}`;
                return (
                    <Link to={detailLink}>
                        <IconButton color="primary">
                            <InfoOutlined />
                        </IconButton>
                    </Link>
                );
            },
            ignoreRowClick: true,
            right: true,
        },
    ];

    const [
        listShipmentActions,
        { data: shipmentActionRes, isLoading, isFetching, error },
    ] = useLazyGetShipmentActionListForUserQuery();

    const isBusy = isLoading || isFetching;

    React.useEffect(() => {
        listShipmentActions({
            page: currentPage,
            limit: perPage,
            sortedBy,
            sortDirection,
            ...(keyword && { keyword }),
        });
    }, [currentPage, keyword, listShipmentActions, perPage, sortedBy, sortDirection]);

    useAppMeta("Shipment Actions");

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={isBusy}>
            <BreadcrumbLayout.Error error={error} />

            <BreadcrumbLayout.Paper>
                <TableFilter
                    filterMineKey={false}
                    searchLabel="Action ID"
                    disabled={isBusy}
                >
                    <ParamsSortBy
                        sortOptions={SHIPMENT_ACTION_SORT_OPTIONS}
                        disabled={isBusy}
                    />
                </TableFilter>

                <AppTable
                    columns={columns}
                    data={shipmentActionRes?.data ?? []}
                    progressPending={isBusy}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={shipmentActionRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default NotificationActionListScreen;